import { X } from "lucide-react";

/**
 * Modal for configuring the debug dashboard (log limit, auto-refresh).
 *
 * Props:
 *   isOpen            - Boolean controlling visibility
 *   onClose           - Callback to close the modal
 *   onApply           - Callback fired by "Apply & Refresh"; receives no args
 *   logLimit          - Current log limit value
 *   onLogLimitChange  - (value: number) => void
 *   autoRefresh       - Boolean
 *   onAutoRefreshChange - (enabled: boolean) => void
 *   refreshInterval   - Refresh interval in seconds
 *   onIntervalChange  - (value: number) => void
 */
const DebugConfigModal = ({
  isOpen,
  onClose,
  onApply,
  logLimit,
  onLogLimitChange,
  autoRefresh,
  onAutoRefreshChange,
  refreshInterval,
  onIntervalChange,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-lg max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="debug-config-modal-title"
      >
        <div className="flex justify-between items-center border-b p-6">
          <h3 id="debug-config-modal-title" className="text-lg font-semibold text-gray-900">
            Debug Configuration
          </h3>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Log Limit</label>
            <input
              type="number"
              min="10"
              max="500"
              value={logLimit}
              onChange={(e) => onLogLimitChange(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Auto Refresh</label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => onAutoRefreshChange(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Enable auto refresh every</span>
              <input
                type="number"
                min="5"
                max="300"
                value={refreshInterval}
                onChange={(e) => onIntervalChange(parseInt(e.target.value))}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
              />
              <span className="text-sm">seconds</span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 border-t p-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={onApply}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Apply & Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebugConfigModal;
